import { useId } from 'react';
import { makeStyles } from '@material-ui/core/styles';

/** The Digistore24 icon mark, in a 26.3 × 27 box. */
const MARK_PATH =
  'M21.8081 0.166687C21.0507 0.170893 20.2943 0.370449 19.617 0.76442L9.65613 6.50621L6.65383 4.77423C4.53341 3.55026 1.82085 4.27417 0.595408 6.39078C0.204088 7.06609 -0.000932979 7.83206 3.19172e-06 8.61206V22.3987V22.4296C0.00749256 24.8555 1.97485 26.824 4.4089 26.8333H4.43277C5.21354 26.8319 5.97932 26.6244 6.65383 26.2328L16.6133 20.491L18.6083 19.339L18.8198 19.2114C20.8691 17.873 21.4439 15.1296 20.1038 13.0831C19.7228 12.5022 19.2112 12.0185 18.6083 11.6722L11.8716 7.79L20.7306 2.70343C21.7753 2.0814 23.1262 2.42302 23.7492 3.46614C23.9575 3.81477 24.0652 4.21389 24.0596 4.62001V18.4071C24.0755 19.6208 23.1028 20.6181 21.8872 20.6336C21.4804 20.6392 21.0798 20.5317 20.7306 20.3237L20.2597 20.0512L18.0583 21.3262L19.6432 22.2407C21.7636 23.4643 24.4752 22.7403 25.7016 20.6233C26.0915 19.9498 26.2966 19.1853 26.2966 18.4071V4.60225C26.2984 3.01188 25.44 1.54395 24.0507 0.76442C23.3734 0.370449 22.6169 0.170893 21.8591 0.166687H21.8081ZM2.53843 23.5535C2.33013 23.2044 2.22247 22.8048 2.22809 22.3987V8.61206C2.21217 7.3979 3.18485 6.40106 4.40047 6.38563C4.80771 6.38003 5.20792 6.48705 5.55711 6.69502L17.5162 13.5883C18.2015 13.9832 18.6223 14.7151 18.6172 15.5054H18.6083C18.6111 16.2971 18.1875 17.0294 17.4989 17.4224L16.618 17.9365L8.77145 13.4131C7.71498 12.8121 7.34613 11.4699 7.94809 10.4146C8.05201 10.2333 8.18073 10.0669 8.33098 9.92203L6.37158 8.79199C4.88588 10.745 5.26737 13.5309 7.22303 15.0137C7.37328 15.1273 7.53009 15.2315 7.69251 15.325L14.4297 19.2072L5.55711 24.3158C5.20371 24.5261 4.81473 24.6261 4.43137 24.6261C3.68149 24.6261 2.95034 24.2433 2.53843 23.5535Z';

/**
 * The mark is one compound path that reads as two interlocked triangles, with
 * gaps where one passes behind the other. For the hover animation each
 * triangle gets its own closed outline instead, so they can move freely with
 * no cut-outs: the left one is the path's outer and inner edge, closed along
 * its own sides; the right one bridges the parts hidden behind the left
 * triangle with curves that meet the visible band and hook tangentially.
 * Both are filled evenodd (outer contour + inner contour).
 */
const LEFT_TRIANGLE =
  'M6.65383 4.77423C4.53341 3.55026 1.82085 4.27417 0.595408 6.39078C0.204088 7.06609 -0.000932979 7.83206 3.19172e-06 8.61206V22.4296C0.00749256 24.8555 1.97485 26.824 4.4089 26.8333H4.43277C5.21354 26.8319 5.97932 26.6244 6.65383 26.2328L16.6133 20.491L18.6083 19.339L18.8198 19.2114C20.8691 17.873 21.4439 15.1296 20.1038 13.0831C19.7228 12.5022 19.2112 12.0185 18.6083 11.6722ZM2.53843 23.5535C2.33013 23.2044 2.22247 22.8048 2.22809 22.3987V8.61206C2.21217 7.3979 3.18485 6.40106 4.40047 6.38563C4.80771 6.38003 5.20792 6.48705 5.55711 6.69502L17.5162 13.5883C18.2015 13.9832 18.6223 14.7151 18.6172 15.5054H18.6083C18.6111 16.2971 18.1875 17.0294 17.4989 17.4224L5.55711 24.3158C5.20371 24.5261 4.81473 24.6261 4.43137 24.6261C3.68149 24.6261 2.95034 24.2433 2.53843 23.5535Z';
const RIGHT_TRIANGLE =
  'M19.617 0.76442L9.65613 6.50621C8.53 7.156 7.098 7.837 6.37158 8.79199C4.88588 10.745 5.26737 13.5309 7.22303 15.0137C7.37328 15.1273 7.53009 15.2315 7.69251 15.325L14.4297 19.2072L18.0583 21.3262L19.6432 22.2407C21.7636 23.4643 24.4752 22.7403 25.7016 20.6233C26.0915 19.9498 26.2966 19.1853 26.2966 18.4071V4.60225C26.2984 3.01188 25.44 1.54395 24.0507 0.76442C23.3734 0.370449 22.6169 0.170893 21.8591 0.166687H21.8081C21.0507 0.170893 20.2943 0.370449 19.617 0.76442ZM11.8716 7.79L20.7306 2.70343C21.7753 2.0814 23.1262 2.42302 23.7492 3.46614C23.9575 3.81477 24.0652 4.21389 24.0596 4.62001V18.4071C24.0755 19.6208 23.1028 20.6181 21.8872 20.6336C21.4804 20.6392 21.0798 20.5317 20.7306 20.3237L20.2597 20.0512L16.618 17.9365L8.77145 13.4131C7.71498 12.8121 7.34613 11.4699 7.94809 10.4146C8.05201 10.2333 8.18073 10.0669 8.33098 9.92203C9.195 9.082 10.744 8.437 11.8716 7.79Z';

// The orange of the Digistore24 logo on the DevOps banner
const ORANGE_LIGHT = '#FFB54D';
const ORANGE = '#FE9D32';
const ORANGE_DEEP = '#F0703F';

const PLAY = '1.4s';
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

/**
 * Orbit: each triangle circles the point midway between the two triangles'
 * centres while staying upright. Halfway round they have traded places; after
 * a full turn they are back where they started.
 *
 * `rotate(θ) translate(o) rotate(-θ) translate(-o)` about that pivot moves a
 * piece whose centre is `o` from the pivot along a circle without turning it,
 * and CSS interpolates the two angles function by function. The triangles'
 * centres are (10.39, 15.53) and (15.91, 11.49), so the pivot is
 * (13.15, 13.51) and the offsets are ±(2.76, −2.02).
 *
 * The intact mark and the two triangles cross-fade over the first and last
 * 8%: at rest the logo is always the original artwork, gaps included.
 */
const orbit = (dx: number, dy: number) => {
  const at = (deg: number) =>
    `rotate(${deg}deg) translate(${dx}px, ${dy}px) rotate(${-deg}deg) translate(${-dx}px, ${-dy}px)`;
  return {
    '0%': { opacity: 0, transform: at(0), scale: '1' },
    '8%, 92%': { opacity: 1 },
    '50%': { scale: '0.9' },
    '100%': { opacity: 0, transform: at(360), scale: '1' },
  };
};
const PIVOT = '13.15px 13.51px';

const useStyles = makeStyles({
  '@keyframes sheen': {
    '0%, 72%': { transform: 'translateX(-34px) skewX(-20deg)' },
    '100%': { transform: 'translateX(40px) skewX(-20deg)' },
  },
  '@keyframes left': orbit(-2.76, 2.02),
  '@keyframes right': orbit(2.76, -2.02),
  '@keyframes whole': {
    '0%, 100%': { opacity: 1 },
    '8%, 92%': { opacity: 0 },
  },
  mark: {
    overflow: 'visible',
    filter: 'drop-shadow(0 0 6px rgba(254, 157, 50, 0.35))',
  },
  whole: {
    'svg:hover &': { animation: `$whole ${PLAY} linear both` },
  },
  piece: {
    opacity: 0,
    // view-box: the mark's own <svg> below, so the pivot is in mark units
    transformBox: 'view-box',
    transformOrigin: PIVOT,
  },
  leftPiece: {
    'svg:hover &': { animation: `$left ${PLAY} ${EASE} both` },
  },
  rightPiece: {
    'svg:hover &': { animation: `$right ${PLAY} ${EASE} both` },
  },
  sheen: {
    animation: '$sheen 7s ease-in-out 1.2s infinite',
    'svg:hover &': { visibility: 'hidden' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    whole: { 'svg:hover &': { animation: 'none' } },
    leftPiece: { 'svg:hover &': { animation: 'none' } },
    rightPiece: { 'svg:hover &': { animation: 'none' } },
    sheen: { animation: 'none' },
  },
});

/**
 * The icon mark with the banner's orange gradient and a slow light sheen, in
 * a 26.3 × 27 box at the parent's origin. Hovering the parent `<svg>` sends
 * the two triangles round each other; they always come to rest as the
 * original mark.
 */
export const LogoMark = () => {
  const classes = useStyles();
  // Both logos can be in the DOM during the sidebar's open/close transition,
  // so gradient and clip ids must be unique per instance
  const id = useId().replace(/:/g, '');
  // userSpaceOnUse, so the moving triangles carry exactly the intact mark's
  // colours
  const fill = `url(#${id}-fill)`;
  return (
    <svg
      className={classes.mark}
      width="26.3"
      height="27"
      viewBox="0 0 26.3 27"
    >
      <defs>
        <linearGradient
          id={`${id}-fill`}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0.17"
          x2="26.3"
          y2="26.83"
        >
          <stop offset="0" stopColor={ORANGE_LIGHT} />
          <stop offset="0.5" stopColor={ORANGE} />
          <stop offset="1" stopColor={ORANGE_DEEP} />
        </linearGradient>
        <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <path d={MARK_PATH} />
        </clipPath>
      </defs>
      <path className={classes.whole} d={MARK_PATH} fill={fill} />
      <path
        className={`${classes.piece} ${classes.rightPiece}`}
        d={RIGHT_TRIANGLE}
        fill={fill}
        fillRule="evenodd"
      />
      <path
        className={`${classes.piece} ${classes.leftPiece}`}
        d={LEFT_TRIANGLE}
        fill={fill}
        fillRule="evenodd"
      />
      <g clipPath={`url(#${id}-clip)`}>
        <rect
          className={classes.sheen}
          x="0"
          y="-4"
          width="9"
          height="35"
          fill={`url(#${id}-sheen)`}
          transform="translate(-34) skewX(-20)"
        />
      </g>
    </svg>
  );
};
